import { Role } from "./Role";

/**
 * Which portal a signed-in user belongs to. The app used to mount one of three separate route
 * trees per audience; now a single static tree serves all three and this is what tells the
 * route elements apart.
 *
 * Deliberately free of React and of any app module: route loaders resolve the audience outside
 * React (see src/api/identityCache.ts), and importing the hook there would put a cycle through
 * `useCerberus` into the middle of the router's module graph.
 */
export type Audience = "clinical" | "patient" | "external" | "caregiver";

/** Everyone who is neither a patient nor an external portal user. */
const CLINICAL_ROLES = [
  Role.Provider,
  Role.Admin,
  Role.Developer,
  Role.Nurse,
  Role.MedicalAssistant,
  Role.AuthTeam,
  Role.PacTeam,
  Role.MtcDoc,
];

/**
 * Map a set of role names to a portal.
 *
 * The precedence (patient, then external, then clinical) is load-bearing and must match the
 * order the old `Routing.tsx` dispatcher used — a user carrying roles from more than one group
 * lands in the same portal they do today.
 */
export function audienceForRoles(
  roles: readonly string[] | undefined,
): Audience | null {
  if (!roles?.length) return null;

  const has = (role: Role) => roles.includes(role);

  if (has(Role.Patient)) return "patient";

  // Both referring providers and their delegated office staff live in the external portal.
  if (has(Role.ExternalProvider) || has(Role.ExternalOrgUser)) return "external";

  // A caregiver is a distinct login (never combined with Patient), scoped to a linked patient.
  if (has(Role.Caregiver)) return "caregiver";

  if (CLINICAL_ROLES.some(has)) return "clinical";

  return null;
}
